import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Loader2, Settings, UserPlus, X } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import DashboardSettingsModal from './meeting/DashboardSettingsModal';
import {
  DEFAULT_MEETING_NOTIFICATION_CADENCE,
  MEETING_REMINDER_OPTIONS,
  describeReminderMinutes,
  normalizeReminderCadence,
} from '../utils/meetingReminderCadence';

const DEFAULT_COMMITTEE_MEETING_NOTIFICATION_SUBJECT = 'Committee Meeting Reminder: {{meeting.title}}';

const CommitteeSettings = () => {
  const navigate = useNavigate();
  const { committeeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [committees, setCommittees] = useState([]);
  const [accessError, setAccessError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({ name: '', description: '', color_hex: '#7C3AED' });
  const [teamMembers, setTeamMembers] = useState([]);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [newMemberId, setNewMemberId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [chairId, setChairId] = useState('');
  const [committeeSettings, setCommitteeSettings] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showPinnedResourcesModal, setShowPinnedResourcesModal] = useState(false);
  const [meetingReminderCadence, setMeetingReminderCadence] = useState(DEFAULT_MEETING_NOTIFICATION_CADENCE);

  useEffect(() => {
    const fetchCommittees = async () => {
      setLoading(true);
      try {
        const response = await api.get('/governance/access');
        setCommittees(response.data?.committees || []);
        setAccessError(!(response.data?.committees_enabled));
      } catch (err) {
        console.error('Failed to load committees:', err);
        setCommittees([]);
        setAccessError(true);
      } finally {
        setLoading(false);
      }
    };

    const fetchTeamMembers = async () => {
      try {
        const response = await api.get('/team');
        const rawMembers = response.data?.members || response.data?.team_members || [];
        const normalized = rawMembers.map((member) => {
          const user = member.user || member;
          return {
            id: user.id || member.id,
            name: user.name || member.name || '',
            email: user.email || member.email || '',
          };
        }).filter((member) => member.id);
        setTeamMembers(normalized);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    };

    const fetchProjects = async () => {
      try {
        const response = await api.get('/projects?client_id=null');
        setProjects(response.data?.projects || response.data || []);
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    };

    fetchCommittees();
    fetchTeamMembers();
    fetchProjects();
    window.scrollTo(0, 0);
  }, []);

  const committee = useMemo(
    () => committees.find((item) => item.id === committeeId) || null,
    [committees, committeeId]
  );

  useEffect(() => {
    if (!committee) return;
    const fetchMembers = async () => {
      try {
        const response = await api.get(`/governance/committees/${committee.id}/members`);
        const members = response.data?.members || [];
        setCommitteeMembers(members);
        const chair = members.find((m) => m.role === 'chair');
        setChairId(chair?.user_id || '');
      } catch (err) {
        console.error('Failed to load committee members:', err);
      }
    };
    const fetchSettings = async () => {
      try {
        const response = await api.get(`/governance/committees/${committee.id}/settings`);
        setCommitteeSettings(response.data);
        setMeetingReminderCadence(normalizeReminderCadence(response.data?.meeting_notification_reminder_minutes || DEFAULT_MEETING_NOTIFICATION_CADENCE));
      } catch (err) {
        console.error('Failed to load committee settings:', err);
      }
    };
    fetchMembers();
    fetchSettings();
  }, [committee]);

  useEffect(() => {
    if (!committee) return;
    setFormState({
      name: committee.name || '',
      description: committee.description || '',
      color_hex: committee.color_hex || '#7C3AED',
    });
  }, [committee]);

  const handleSave = async () => {
    if (!committee) return;
    setSaving(true);
    try {
      await api.put(`/governance/committees/${committee.id}`, {
        name: formState.name.trim() || committee.name,
        description: formState.description.trim() || null,
        color_hex: formState.color_hex || null,
      });
      if (committeeSettings) {
        await api.put(`/governance/committees/${committee.id}/settings`, {
          ...committeeSettings,
          meeting_notification_reminder_minutes: normalizeReminderCadence(meetingReminderCadence),
        });
      }
      setCommittees(prev => prev.map(c => c.id === committee.id ? { ...c, ...formState } : c));
      toast.success('Committee settings saved');
    } catch (err) {
      console.error('Failed to save committee settings:', err);
      toast.error('Failed to save committee settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!committee || !newMemberId) return;
    setSaving(true);
    try {
      await api.post(`/governance/committees/${committee.id}/members`, {
        user_id: newMemberId,
        role: newMemberRole || 'member',
      });
      const response = await api.get(`/governance/committees/${committee.id}/members`);
      setCommitteeMembers(response.data?.members || []);
      setNewMemberId('');
      setNewMemberRole('member');
      toast.success('Member added to committee');
    } catch (err) {
      console.error('Failed to add committee member:', err);
      toast.error(err.response?.data?.detail || 'Failed to add committee member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!committee) return;
    setSaving(true);
    try {
      await api.delete(`/governance/committees/${committee.id}/members/${memberUserId}`);
      setCommitteeMembers(prev => prev.filter(m => m.user_id !== memberUserId));
    } finally {
      setSaving(false);
    }
  };

  const handleChairChange = async (userId) => {
    if (!committee || !userId) return;
    setSaving(true);
    try {
      await api.put(`/governance/committees/${committee.id}/chair`, { user_id: userId });
      const response = await api.get(`/governance/committees/${committee.id}/members`);
      const members = response.data?.members || [];
      setCommitteeMembers(members);
      const chair = members.find((m) => m.role === 'chair');
      setChairId(chair?.user_id || '');
      toast.success('Committee chair updated');
    } catch (err) {
      console.error('Failed to update committee chair:', err);
      toast.error('Failed to update committee chair');
    } finally {
      setSaving(false);
    }
  };

  const availableMembers = teamMembers.filter((member) => (
    !committeeMembers.some((cm) => cm.user_id === member.id)
  ));

  const effectiveSettings = committeeSettings || {
    followup_project_id: null,
    followup_task_roles: ['chair', 'secretary'],
    followup_task_allow_executive: true,
    followup_task_allow_admins: true,
    meeting_start_allow_chair: true,
    meeting_start_allow_admins: true,
    meeting_start_allow_executive: true,
    meeting_notifications_enabled: true,
    meeting_notification_subject: DEFAULT_COMMITTEE_MEETING_NOTIFICATION_SUBJECT,
    meeting_notification_reminder_minutes: DEFAULT_MEETING_NOTIFICATION_CADENCE,
  };

  const updateSetting = (key, value) => {
    setCommitteeSettings((prev) => ({
      ...(prev || effectiveSettings),
      [key]: value,
    }));
  };

  const FOLLOWUP_ROLE_OPTIONS = [
    { value: 'chair', label: 'Chair' },
    { value: 'vice_chair', label: 'Vice Chair' },
    { value: 'secretary', label: 'Secretary' },
    { value: 'member', label: 'Member' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Committees unavailable</h2>
          <p className="text-gray-500 mb-4">Committee access is disabled or unavailable.</p>
          <Button onClick={() => navigate('/governance')}>Back to Governance</Button>
        </div>
      </div>
    );
  }

  if (!committee) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Committee not found</h2>
          <Button onClick={() => navigate('/governance')}>Back to Governance</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex-1 w-full" data-testid="committee-settings">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/governance/committees/${committeeId}`)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Committee
              </Button>
              <div className="border-l border-gray-200 h-6" />
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Settings className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{committee.name} Settings</h1>
                  <p className="text-sm text-gray-500">Committee-specific configuration</p>
                </div>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Committee Details</h3>
          <p className="text-sm text-gray-500 mb-6">
            Update committee information and visibility settings. (Editing coming soon)
          </p>
          <div className="space-y-4">
            <div>
              <Label>Committee Name</Label>
              <Input
                className="mt-1"
                value={formState.name}
                onChange={(e) => setFormState({ ...formState, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Primary Color</Label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  value={formState.color_hex || '#7C3AED'}
                  onChange={(e) => setFormState({ ...formState, color_hex: e.target.value })}
                  className="h-9 w-12 rounded border border-gray-200 bg-white"
                  aria-label="Committee color"
                />
                <Input
                  value={formState.color_hex || '#7C3AED'}
                  onChange={(e) => setFormState({ ...formState, color_hex: e.target.value.toUpperCase() })}
                  className="w-32"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                className="mt-1"
                value={formState.description}
                onChange={(e) => setFormState({ ...formState, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Pinned Resources</h3>
            <Button onClick={() => setShowPinnedResourcesModal(true)}>
              Configure
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Configure quick links that appear in committee meetings under Pinned Resources.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Committee Chair</h3>
          <p className="text-sm text-gray-500 mb-4">
            Chairs and admins can manage committee settings and workflows.
          </p>
          <Select value={chairId} onValueChange={handleChairChange}>
            <SelectTrigger className="max-w-sm">
              <SelectValue placeholder="Select chair..." />
            </SelectTrigger>
            <SelectContent>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name || member.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Permissions</h3>
          <p className="text-sm text-gray-500 mb-6">
            Configure who can start and control committee meetings.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">Chairs can start meetings</p>
                <p className="text-sm text-gray-500">Allow committee chairs to start and run meetings</p>
              </div>
              <Switch
                checked={effectiveSettings.meeting_start_allow_chair}
                onCheckedChange={(checked) => updateSetting('meeting_start_allow_chair', checked)}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Admins can start meetings</p>
                <p className="text-sm text-gray-500">Allow team admins to start committee meetings</p>
              </div>
              <Switch
                checked={effectiveSettings.meeting_start_allow_admins}
                onCheckedChange={(checked) => updateSetting('meeting_start_allow_admins', checked)}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Executive Committee can start meetings</p>
                <p className="text-sm text-gray-500">Allow executive committee members to start meetings</p>
              </div>
              <Switch
                checked={effectiveSettings.meeting_start_allow_executive}
                onCheckedChange={(checked) => updateSetting('meeting_start_allow_executive', checked)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Notification Settings</h3>
          <p className="text-sm text-gray-500 mb-6">
            Configure shared reminders for this committee's meetings. These settings apply to all committee members.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">Committee meeting reminders enabled</p>
                <p className="text-sm text-gray-500">Send committee meeting reminders to all committee members using this shared configuration.</p>
              </div>
              <Switch
                checked={effectiveSettings.meeting_notifications_enabled ?? true}
                onCheckedChange={(checked) => updateSetting('meeting_notifications_enabled', checked)}
              />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <Label>Reminder email subject</Label>
              <Input
                className="mt-1 max-w-2xl"
                value={effectiveSettings.meeting_notification_subject ?? DEFAULT_COMMITTEE_MEETING_NOTIFICATION_SUBJECT}
                onChange={(e) => updateSetting('meeting_notification_subject', e.target.value)}
                placeholder={DEFAULT_COMMITTEE_MEETING_NOTIFICATION_SUBJECT}
              />
              <p className="text-xs text-gray-400 mt-1">Available placeholders: {'{{meeting.title}}'}, {'{{meeting.date}}'}, {'{{meeting.time}}'}.</p>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div>
                <Label>Reminder cadence</Label>
                <p className="text-xs text-gray-400 mt-1">Choose when shared committee meeting reminders should go out. Saved internally as minutes before the meeting.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                {MEETING_REMINDER_OPTIONS.map((option) => {
                  const checked = meetingReminderCadence.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${checked ? 'border-purple-300 bg-purple-50 text-purple-900' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setMeetingReminderCadence((prev) => normalizeReminderCadence(
                          e.target.checked
                            ? [...prev, option.value]
                            : prev.filter((value) => value !== option.value)
                        ))}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                {meetingReminderCadence.length > 0 ? meetingReminderCadence.map((minutes) => (
                  <span key={minutes} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                    {describeReminderMinutes(minutes)}
                  </span>
                )) : (
                  <p className="text-xs text-amber-600">No reminders selected. Meetings will save without reminder offsets.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Follow-up Task Generation</h3>
          <p className="text-sm text-gray-500 mb-6">
            Choose the project for follow-up tasks and configure who can generate them.
          </p>
          <div className="space-y-6">
            <div className="max-w-xl">
              <Label>Follow-up Project</Label>
              <Select
                value={effectiveSettings.followup_project_id || ""}
                onValueChange={(value) =>
                  updateSetting('followup_project_id', value === 'none' ? null : value)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Allowed roles</Label>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FOLLOWUP_ROLE_OPTIONS.map((option) => {
                  const currentRoles = effectiveSettings.followup_task_roles || [];
                  const checked = currentRoles.includes(option.value);
                  return (
                    <label key={option.value} className="flex items-center space-x-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...currentRoles, option.value]
                            : currentRoles.filter((role) => role !== option.value);
                          updateSetting('followup_task_roles', next);
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-gray-900">Allow admins</p>
                  <p className="text-sm text-gray-500">Admins can generate follow-up tasks</p>
                </div>
                <Switch
                  checked={effectiveSettings.followup_task_allow_admins}
                  onCheckedChange={(checked) => updateSetting('followup_task_allow_admins', checked)}
                />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Allow executive committee</p>
                  <p className="text-sm text-gray-500">Executive members can generate follow-up tasks</p>
                </div>
                <Switch
                  checked={effectiveSettings.followup_task_allow_executive}
                  onCheckedChange={(checked) => updateSetting('followup_task_allow_executive', checked)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Committee Members</h3>
              <p className="text-sm text-gray-500">Manage committee membership and roles.</p>
            </div>
            <div className="text-sm text-gray-500">
              {committeeMembers.length} members
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Select value={newMemberId} onValueChange={setNewMemberId}>
              <SelectTrigger>
                <SelectValue placeholder="Select member..." />
              </SelectTrigger>
              <SelectContent>
                {availableMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name || member.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={newMemberRole} onValueChange={setNewMemberRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="vice_chair">Vice Chair</SelectItem>
                <SelectItem value="secretary">Secretary</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleAddMember} disabled={!newMemberId || saving}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          </div>

          <div className="space-y-2">
            {committeeMembers.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-6">No committee members yet.</div>
            ) : (
              committeeMembers.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{member.name || member.email}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 capitalize">{member.role}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.user_id)}
                      disabled={saving || member.role === 'chair'}
                    >
                      <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {committee && (
        <DashboardSettingsModal
          open={showPinnedResourcesModal}
          onClose={() => setShowPinnedResourcesModal(false)}
          settingsEndpointBase={`/governance/committees/${committee.id}/dashboard-settings`}
          documentsEndpoint={`/board/documents?committee_id=${committee.id}`}
        />
      )}
    </div>
  );
};

export default CommitteeSettings;
